import { Alert } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { PermissionsService } from '../../auth/services/PermissionsService';
import { InlineLoader } from '../../common/components/InlineLoader';
import { Dialog, DialogBody } from '../../common/components/Modal/Modal';
import { getErrorMessage } from '../../common/errors/errors';
import { UserLevel } from '../../organization/users/enums/UserLevel';
import { OrganizationUsersRepository } from '../../organization/users/services/OrganizationUsersRepository';
import { UpdateUserForm } from '../components/UpdateUserForm';
import { UserRole } from '../../organization/users/enums/UserRole';
import { getFeatureFlag, FeatureFlagsType } from '../../config/launch-darkly/FeatureFlagsController';

type Props = {
  userId: number;
  currentPreferredName: string | null;
  currentLevel: UserLevel;
  currentRoles: UserRole[];
  isCurrentUser: boolean;

  onClosed: (userId: number | undefined) => void;
};

type FormPermissions = {
  canUpdatePreferredName: boolean;
  canUpdatePermissions: boolean;
};

export const UpdateUserFlow = ({ userId, currentPreferredName, currentLevel, currentRoles, isCurrentUser, onClosed }: Props) => {
  const [userService] = useState(new OrganizationUsersRepository());
  const [permissionService] = useState(new PermissionsService());

  const [error, setError] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<FormPermissions | null>(null);

  const useRoles = Boolean(getFeatureFlag(FeatureFlagsType.ENABLE_ORGANIZATION_USER_ROLES));

  useEffect(() => {
    const load = async () => {
      try {
        const [canUpdatePreferredName, canUpdatePermissions] = await Promise.all([
          permissionService.canUpdateUserPreferredName(userId),
          permissionService.canUpdateUserPermissions(userId),
        ]);
        setPermissions({ canUpdatePreferredName, canUpdatePermissions });
      } catch (e: unknown) {
        setError(getErrorMessage(e));
      }
    };

    load();

    return () => {
      userService.cancelAllRequests();
      permissionService.cancelAllRequests();
    };
  }, [userId, permissionService, userService]);

  const handleSubmit = async (preferredName: string | null, level: UserLevel | null, roles: UserRole[] | null) => {
    try {
      setError(null);
      if (preferredName !== null || level !== null || roles !== null) {
        const user =
          level === null
            ? await userService.updateUser({ userId, preferredName, level: null, roles })
            : await userService.updateUser({ userId, preferredName, level, roles: null });
        onClosed(user.getId());
      } else {
        onClosed(undefined);
      }
    } catch (e: unknown) {
      setError(getErrorMessage(e));
    }
  };

  return (
    <Dialog title={t('updateUser.title')} subtitle={t('updateUser.subtitle')} onDismiss={() => onClosed(undefined)}>
      <DialogBody>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!permissions && !error && <InlineLoader />}
        {!!permissions && (
          <UpdateUserForm
            useRoles={useRoles}
            prefill={{
              preferredName: currentPreferredName ?? '',
              level: currentLevel,
              roles: currentRoles,
              preferredNameDisabled: !permissions.canUpdatePreferredName,
              levelDisabled: !permissions.canUpdatePermissions,
              isCurrentUser,
            }}
            submit={{ label: t('updateUser.submit'), action: handleSubmit }}
            back={{ label: t('updateUser.back'), action: () => onClosed(undefined) }}
          />
        )}
      </DialogBody>
    </Dialog>
  );
};
